import { useEffect, useRef, useState } from "react";

/**
 * TurnstileWidget — Cloudflare Turnstile CAPTCHA that lazy-loads the script
 * only when a site key is provided. Renders nothing (returns null) when
 * captcha is disabled, so pre-launch dev flows are unaffected.
 *
 * Props:
 *   siteKey  – public Turnstile site key (empty string = disabled)
 *   onToken  – called with the token string on successful challenge; called
 *              with "" when the token expires so the parent knows to re-lock
 *              its submit button.
 *   theme    – "light" | "dark" | "auto" (default: light — matches our UI)
 *
 * We DELIBERATELY don't error out if Cloudflare's script fails to load
 * (network block, ad-blocker, etc.). Instead we show a small inline notice
 * so the user can retry — signup falls through on backend if `turnstile_token`
 * is missing and captcha is required, giving a clear error.
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

function loadScript() {
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
}

export default function TurnstileWidget({ siteKey, onToken, theme = "light" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    setStatus("loading");
    loadScript()
      .then(() => {
        if (cancelled) return;
        const render = () => {
          if (!window.turnstile || !containerRef.current) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme,
            callback: (token) => onToken?.(token),
            "expired-callback": () => onToken?.(""),
            "error-callback": () => onToken?.(""),
          });
          setStatus("ready");
        };
        // The script defines a global turnstile.ready() when loaded async
        if (window.turnstile?.ready) {
          window.turnstile.ready(render);
        } else {
          render();
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.warn("[Turnstile] widget cleanup failed:", err?.message || err);
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, onToken]);

  if (!siteKey) return null;

  return (
    <div className="mt-2" data-testid="turnstile-widget">
      <div ref={containerRef} />
      {status === "loading" && (
        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
          Loading spam check…
        </div>
      )}
      {status === "error" && (
        <div className="text-[11px] mt-1" style={{ color: "var(--error-text, #B91C1C)" }}>
          Couldn't load the spam check. Disable ad-blockers and refresh.
        </div>
      )}
    </div>
  );
}
